import type { Bot } from '../types/api.types';
import type { BotItem } from '../../features/assistant-hub/types';

/**
 * Transforms a Bot API response to a BotItem view model
 * @param bot - The Bot object from the API
 * @param isFavourite - Whether the bot is marked as a favourite for the current user
 * @param currentUserEmail - Email of the current user to determine ownership
 * @returns BotItem for use in UI components
 */
export const mapBotToBotItem = (
  bot: Bot,
  isFavourite = false,
  currentUserEmail?: string
): BotItem => ({
  id: bot._id,
  title: bot.bot_name,
  description: bot.description,
  division: bot.division,
  workspace: bot.workspace || undefined,
  isFavourite,
  createdBy:
    currentUserEmail && bot.owner_email === currentUserEmail
      ? 'current-user'
      : undefined,
  sessionId: bot.sessionId,
  capabilities: bot.capabilities,
  chatEndpoint: bot.chatEndpoint,
  ragEnabled: bot.capabilities?.rag,
  multimodalEnabled: bot.capabilities?.multimodal,
  webSearchEnabled: bot.capabilities?.webSearch,
  webMediaEnabled: bot.capabilities?.webMedia,
});

/**
 * Transforms an array of Bots to an array of BotItems
 * @param bots - Array of Bot objects from the API
 * @param favouriteIds - Array of bot IDs that are marked as favourites
 * @param currentUserEmail - Email of the current user to determine ownership
 * @returns Array of BotItems for use in UI components
 */
export const mapBotsToBotItems = (
  bots: Bot[],
  favouriteIds: string[] = [],
  currentUserEmail?: string
): BotItem[] => {
  return bots.map(bot =>
    mapBotToBotItem(bot, favouriteIds.includes(bot._id), currentUserEmail)
  );
};

/**
 * Parses conversation title that can be either a string or an array of text objects
 * @param title - The title from the conversation API response
 * @returns A clean string representation of the title
 */
export const parseConversationTitle = (title: unknown): string => {
  // If title is already a string, return it directly
  if (typeof title === 'string') {
    return title;
  }

  // If title is an array, extract text from objects with type "text"
  if (Array.isArray(title)) {
    const textParts: string[] = [];

    for (const item of title) {
      if (
        typeof item === 'object' &&
        item !== null &&
        'type' in item &&
        'text' in item
      ) {
        if (item.type === 'text' && typeof item.text === 'string') {
          textParts.push(item.text);
        }
      }
    }

    // Join text parts with a space, or return a fallback if no text found
    return textParts.length > 0 ? textParts.join(' ') : 'Untitled Conversation';
  }

  // Fallback for any other unexpected format
  return 'Untitled Conversation';
};
